(function(root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (typeof window !== "undefined") window.WTEIngredientCosting = api;
})(typeof globalThis !== "undefined" ? globalThis : this, function() {
  "use strict";
  const round = (value) => Math.round(value * 100) / 100;

  function packageCost(pack, currency) {
    if (!pack || pack.currency !== currency || !Number.isFinite(pack.size) || pack.size <= 0 ||
        !Number.isFinite(pack.price) || pack.price < 0 || !Number.isFinite(pack.yieldPercent) ||
        pack.yieldPercent <= 0 || pack.yieldPercent > 100) return null;
    const usable = pack.size * pack.yieldPercent / 100;
    return { usable, unitCost: pack.price / usable };
  }

  function costLines(lines, ingredients, currency) {
    const missing = [];
    let total = 0;
    const rows = lines.map((line) => {
      const ingredient = ingredients.find((row) => row.id === line.ingredientId);
      const pack = ingredient && packageCost(ingredient.package, currency);
      if (!pack || ingredient.unit !== line.unit) missing.push(ingredient ? ingredient.name : line.ingredientId);
      const cost = pack ? line.quantity * pack.unitCost : null;
      if (cost !== null) total += cost;
      return { ...line, name: ingredient ? ingredient.name : line.ingredientId,
        unitCost: pack ? pack.unitCost : null, cost: cost === null ? null : round(cost),
        servingsPerPackage: pack ? Math.floor(pack.usable / line.quantity) : null };
    });
    return { rows, total: missing.length ? null : round(total), missing };
  }

  function selectedRecipe(recipe, portion, extras) {
    if (!recipe) return { error: "Recipe quantities not recorded." };
    const selected = [...recipe.base];
    const sections = [recipe.base];
    const adjustment = recipe.portions.find((row) => row.key === portion);
    if (adjustment) {
      selected.push(...adjustment.lines);
      sections.push(adjustment.lines);
    }
    else if (portion !== "Regular") return { error: `Portion quantities not recorded: ${portion}.` };
    for (const key of extras) {
      const extra = recipe.extras.find((row) => row.key === key);
      if (!extra) return { error: `Add-on quantities not recorded: ${key}.` };
      selected.push(...extra.lines);
      sections.push(extra.lines);
    }
    const grouped = new Map();
    for (const line of selected) {
      const existing = grouped.get(line.ingredientId);
      if (existing) existing.quantity += line.quantity;
      else grouped.set(line.ingredientId, { ...line });
    }
    return { lines: Array.from(grouped.values()), sections };
  }

  function businessOverhead(settings, currency) {
    if (!settings) return { error: "Save monthly business costs and expected meals first." };
    if (settings.currency !== currency) return { error: "Business-cost currency must match the meal; no conversion is performed." };
    const amounts = ["rent", "staff", "utilities", "other"].map((key) => settings[key]);
    if (amounts.some((value) => !Number.isFinite(value) || value < 0 || value > 100000000) ||
        !Number.isInteger(settings.expectedMeals) || settings.expectedMeals < 1 || settings.expectedMeals > 1000000) {
      return { error: "Enter all monthly costs (including explicit zero) and 1 to 1000000 expected meals." };
    }
    const monthly = round(amounts.reduce((sum, value) => sum + value, 0));
    return { monthly, perMeal: monthly / settings.expectedMeals };
  }

  function fullCostPrice(foodCost, price, target, settings, currency) {
    const overhead = businessOverhead(settings, currency);
    if (overhead.error) return overhead;
    if (!Number.isFinite(foodCost) || foodCost < 0 || !Number.isFinite(price) || price < 0 ||
        !Number.isFinite(target) || target < 0 || target >= 100) {
      return { error: "Enter valid meal costs, selling price and a full-cost target margin from 0% to below 100%." };
    }
    const total = foodCost + overhead.perMeal;
    const suggestedPrice = Math.ceil((total / (1 - target / 100) - 1e-9) * 100) / 100;
    return { monthly: overhead.monthly, overheadPerMeal: overhead.perMeal, totalCost: total,
      profit: price - total, margin: price > 0 ? 100 * (price - total) / price : null, suggestedPrice };
  }

  function mealStock(recipe, ingredients, portion, extras, servings = 1) {
    if (!Number.isInteger(servings) || servings < 1 || servings > 100000) {
      return { error: "Plan 1 to 100000 whole servings." };
    }
    const selected = selectedRecipe(recipe, portion, extras);
    if (selected.error) return selected;
    if (!selected.lines.length) return { error: "Record at least one base ingredient before estimating stock." };
    const rows = selected.lines.map((line) => {
      const ingredient = ingredients.find((row) => row.id === line.ingredientId);
      const stock = ingredient?.expectedOnHand;
      const yieldPercent = ingredient?.package?.yieldPercent;
      const known = ingredient && ingredient.unit === line.unit && Number.isFinite(stock) &&
        Number.isFinite(yieldPercent) && yieldPercent > 0 && yieldPercent <= 100;
      const usable = known ? Math.max(0, stock) * yieldPercent / 100 : null;
      const difference = known ? line.quantity * servings - usable : null;
      return { ...line, name: ingredient?.name || line.ingredientId, stock: Number.isFinite(stock) ? stock : null,
        required: line.quantity * servings, usable,
        shortage: known ? difference > 1e-9 ? difference : 0 : null,
        portions: known ? Math.floor((usable + 1e-9) / line.quantity) : null };
    });
    const unknown = rows.some((row) => row.portions === null);
    return { rows, servings, portions: unknown ? null : Math.min(...rows.map((row) => row.portions)),
      status: rows.some((row) => row.shortage > 0) ? "shortage" : unknown ? "unknown" : "sufficient" };
  }

  function estimate(recipe, ingredients, currency, portion, extras, packaging, price, target) {
    const selected = selectedRecipe(recipe, portion, extras);
    if (selected.error) return selected;
    const ingredientsCost = costLines(selected.lines, ingredients, currency);
    if (ingredientsCost.total === null) return { ...ingredientsCost, error: `Package cost not recorded in ${currency}: ${ingredientsCost.missing.join(", ")}.` };
    // Match the independently rounded base/option costs saved for order snapshots.
    ingredientsCost.total = round(selected.sections.reduce((sum, rows) => sum + costLines(rows, ingredients, currency).total, 0));
    if (!Number.isFinite(packaging) || packaging < 0 || packaging > 100000) {
      return { ...ingredientsCost, error: "Enter a valid packaging cost, including explicit zero." };
    }
    if (!Number.isFinite(price) || price < 0) return { ...ingredientsCost, error: "Enter a valid meal selling price." };
    if (!Number.isFinite(target) || target < 0 || target >= 100) {
      return { ...ingredientsCost, error: "Target gross margin must be at least 0% and below 100%." };
    }
    const total = round(ingredientsCost.total + packaging);
    // Round upwards to avoid recommending a price below the requested margin.
    const suggested = Math.ceil((total / (1 - target / 100) - 1e-9) * 100) / 100;
    const profit = round(price - total);
    return { ...ingredientsCost, ingredientCost: ingredientsCost.total, totalCost: total, profit,
      margin: price > 0 ? 100 * profit / price : null, suggestedPrice: suggested };
  }
  return { packageCost, costLines, estimate, businessOverhead, fullCostPrice, mealStock };
});
